import {
  QueryObserver,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import api from "helpers/api";
import endpoints from "helpers/endpoints";
import RunDetails from "types/RunDetails.type";
import Run from "types/Run.type";
import * as constants from "helpers/constants";
import RunSummary from "types/RunSummary.type";
import RunLog from "types/RunLog.type";
import { useAppSelector } from "context/hooks";
import SensorVariable from "types/SensorVariables.types";
import RunComment from "types/RunComment.type";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import SensorDetails from "types/SensorDetails.type";
import { selectIsRunLive, selectSelectedTime } from "context/graph.selector";
import { store } from "context/store";
import queryClient from "helpers/queryClient";

const fetchLiveRuns = () => {
  return api({
    url: endpoints.Runs,
    params: {
      isOnline: true,
      pageNumber: 1,
      rowCount: 1000
    } // hardcoding pageNumber and rowCount for handling static information
  });
};

const useLiveRuns = () => {
  return useQuery({
    queryKey: ["live-runs"],
    queryFn: fetchLiveRuns,
    select: (data): Run[] => data.data.runsData,
    refetchInterval: constants.LIVE_RUN_REFETCH_INTERVAL,
    meta: {
      errorMessage: "E.RUN-001"
    }
  });
};

const fetchRunDetails = ({ queryKey }) => {
  const runId = queryKey[1];

  return api({
    url: endpoints.RunDetails(runId)
  });
};

const useRunDetails = () => {
  const { runId } = useParams();
  const isRunLive = useAppSelector(selectIsRunLive);

  return useQuery({
    queryKey: ["run-details", runId],
    queryFn: fetchRunDetails,
    select: (data): RunDetails => data.data,
    meta: {
      errorMessage: "E.RUN-002"
    },
    refetchInterval: isRunLive ? constants.RUN_DETAILS_REFETCH_INTERVAL : false
  });
};

const fetchRunSummary = ({ queryKey }) => {
  const runId = queryKey[1];

  return api({
    url: endpoints.RunSummary(runId)
  });
};

const useRunSummary = () => {
  const { runId } = useParams();
  const isRunLive = useAppSelector(selectIsRunLive);

  return useQuery({
    queryKey: ["run-summary", runId],
    queryFn: fetchRunSummary,
    select: (data): RunSummary => data.data,
    meta: {
      errorMessage: "E.RUN-003"
    },
    refetchInterval: isRunLive ? constants.RUN_SUMMARY_REFETCH_INTERVAL : false
  });
};

const fetchRunLogs = ({ queryKey }) => {
  const runId = queryKey[1];

  return api({
    url: endpoints.RunLogs(runId)
  });
};

const useRunLogs = () => {
  const isRunLive = useAppSelector(selectIsRunLive);
  const { runId } = useParams();

  return useQuery({
    queryKey: ["run-logs", runId],
    queryFn: fetchRunLogs,
    select: (data): RunLog[] => data.data.runLogData,
    meta: {
      errorMessage: "E.RUN-007"
    },
    refetchInterval: isRunLive ? constants.RUN_LOG_REFETCH_INTERVAL : false
  });
};

const fetchRunType = () => {
  return api({
    url: endpoints.RunType,
    method: "get"
  });
};

const useRunType = () => {
  return useQuery({
    queryKey: ["run-type"],
    queryFn: fetchRunType,
    select: (data): { id: number; name?: string }[] => data.data.runTypes,
    meta: {
      errorMessage: "E.RUN-003"
    }
  });
};

const fetchRunTimeSnap = (runId, selected, signal) => {
  return api({
    method: "get",
    url: endpoints.RunTimeSnap(runId),
    signal,
    params: {
      timeStamp: dayjs(selected).toISOString()
    }
  });
};

const useRunTimeSnap = () => {
  const { runId } = useParams();
  const selected = useAppSelector(selectSelectedTime);

  return useQuery({
    queryKey: ["run-time-snap", runId, selected],
    queryFn: ({ signal }) => fetchRunTimeSnap(runId, selected, signal),
    select: (data) => data.data.timeSnapSensorDetails,
    enabled: selected != null,
    meta: {
      errorMessage: "E.RUN-010"
    },
    staleTime: constants.RUN_TIME_SNAP_STALE_TIME
  });
};

const fetchRuns = ({ queryKey }) => {
  const fetchOptions = queryKey[1];

  return api({
    method: "get",
    url: endpoints.Runs,
    params: { ...fetchOptions },
    paramsSerializer: {
      indexes: null // by default: false
    }
  });
};

function useRuns(fetchOptions) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["all-runs", fetchOptions],
    queryFn: fetchRuns,
    select: (data): { runsData: Run[]; totalRecords: number } => {
      if (data.data) {
        return data.data;
      } else {
        return { runsData: [], totalRecords: 0 };
      }
    },
    initialData: () => {
      return queryClient.getQueryData(["all-runs"], {
        exact: false,
        type: "active"
      });
    },
    meta: {
      errorMessage: "E.RUN-004"
    },
    keepPreviousData: true
  });
}

const fetchRunComments = ({ queryKey }) => {
  const runId = queryKey[1];

  return api({
    url: endpoints.RunComments(runId)
  });
};

function useRunComments() {
  const { runId } = useParams();
  const isRunLive = useAppSelector(selectIsRunLive);

  return useQuery({
    queryKey: ["run-comments", runId],
    queryFn: fetchRunComments,
    select: (data): RunComment[] => data.data.runComments,
    meta: {
      errorMessage: "E.RUN-008"
    },
    keepPreviousData: true,
    refetchInterval: isRunLive ? constants.RUN_COMMENTS_REFETCH_INTERVAL : false
  });
}

const updateRunComment = ({ id, runId, comment }) => {
  return api({
    url: endpoints.RunComment(runId, id),
    method: "put",
    data: comment,
    headers: { "Content-Type": "application/json" }
  });
};

const useUpdateRunComment = (onRunCommentUpdated) => {
  return useMutation({
    mutationFn: updateRunComment,
    onSettled: onRunCommentUpdated,
    meta: {
      errorMessage: "E.RUN-009"
    }
  });
};

const addNewComment = ({ data, runId }) => {
  return api({
    url: endpoints.RunComments(runId),
    method: "post",
    data
  });
};

const useAddNewComment = (onCommentAdded) => {
  return useMutation({
    mutationFn: addNewComment,
    onSettled: onCommentAdded,
    meta: {
      errorMessage: "E.RUN-011"
    }
  });
};

function fetchSensorData(runId, variables) {
  return api({
    method: "get",
    url: endpoints.RunSensors(runId),
    params: {
      filter: variables
    },
    paramsSerializer: {
      indexes: null // by default: false
    }
  });
}

function watchRunSensors(runId, variables, onSuccess) {
  const isRunLive = selectIsRunLive(store.getState());
  const observer = new QueryObserver(queryClient, {
    queryKey: ["run-sensors", runId, [...variables]],
    queryFn: () => fetchSensorData(runId, variables),
    select: (data): SensorDetails[] => {
      if (data.data) {
        const filterSensorData = data.data.sensorData.map((item) => {
          if (item.graphGroup === constants.BP_ENCODER_GRAPH_GROUP) {
            const filteredReadings = item.readings.filter(
              (reading) =>
                reading.y >= constants.BP_ENCODER_MIN_VALUE &&
                reading.y <= constants.BP_ENCODER_MAX_VALUE
            );
            item = { ...item, readings: filteredReadings };
          }
          return item;
        });
        return filterSensorData;
      } else {
        return [];
      }
    },
    refetchInterval: isRunLive ? constants.RUN_SENSOR_REFETCH_INTERVAL : false,
    meta: { errorMessage: "E.RUNS-005" }
  });

  return observer.subscribe(onSuccess);
}

const fetchSensorVariables = async ({ queryKey }) => {
  const id = queryKey[1];

  return api({
    url: endpoints.RunSensorVariable(id)
  });
};

const useSensorTags = () => {
  const { runId } = useParams();

  return useQuery({
    queryKey: ["sensor-variables", runId],
    queryFn: fetchSensorVariables,
    select: (data): SensorVariable[] => data.data.sensorVariables,
    meta: {
      errorMessage: "E.RUN-006"
    },
    staleTime: constants.SENSOR_VARIABLES_API_STALE_TIME
  });
};

export {
  useLiveRuns,
  useRunDetails,
  useRuns,
  useRunType,
  useRunSummary,
  useRunComments,
  useUpdateRunComment,
  useRunLogs,
  useSensorTags,
  useAddNewComment,
  useRunTimeSnap,
  watchRunSensors
};
